import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { FileText, Save, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-portal-252/artifacts/ymujr84m_Logo%20blitzz%20%2B%20BYB%20ok%20sans%20fond%20copie.png";

const ONBOARDING_SECTIONS = [
  {
    id: "identification", title: "Informations Générales", fields: [
      { name: "raison_sociale", label: "Raison sociale", type: "text", required: true },
      { name: "forme_juridique", label: "Forme juridique", type: "select", options: ["SARL", "SAS", "EURL", "SA", "Auto-entrepreneur", "Autre"] },
      { name: "siret", label: "SIRET", type: "text" },
      { name: "code_ape", label: "Code APE/NAF", type: "text" },
      { name: "adresse", label: "Adresse", type: "text" },
      { name: "code_postal", label: "Code postal", type: "text" },
      { name: "ville", label: "Ville", type: "text" },
      { name: "telephone", label: "Téléphone", type: "text" },
      { name: "site_web", label: "Site web", type: "text" },
    ]
  },
  {
    id: "dirigeant", title: "Dirigeant Principal", fields: [
      { name: "dirigeant_nom", label: "Nom et Prénom", type: "text" },
      { name: "dirigeant_fonction", label: "Fonction", type: "text" },
      { name: "dirigeant_email", label: "Email", type: "text" },
      { name: "dirigeant_telephone", label: "Téléphone", type: "text" },
    ]
  },
  {
    id: "profil", title: "Profil de l'Entreprise", fields: [
      { name: "secteur", label: "Secteur d'activité", type: "select", options: ["Commerce", "Services", "BTP", "Industrie", "Digital", "Santé", "Restauration", "Autre"] },
      { name: "description_activite", label: "Description de l'activité", type: "textarea" },
      { name: "effectif", label: "Effectif total", type: "select", options: ["1-5", "6-10", "11-20", "21-50", "50+"] },
    ]
  },
];

export default function OnboardingPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);

  const updateField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Save as client profile
      await api.put("/client/profile", {
        company_name: formData.raison_sociale || "",
        legal_form: formData.forme_juridique || "",
        siret: formData.siret || "",
        ape_code: formData.code_ape || "",
        address: formData.adresse || "",
        postal_code: formData.code_postal || "",
        city: formData.ville || "",
        phone: formData.telephone || "",
        website: formData.site_web || "",
        sector: formData.secteur || "",
        activity_description: formData.description_activite || "",
        employee_count: formData.effectif || "",
      });
      // Save as document
      await api.post("/documents", {
        client_id: user.user_id,
        doc_type: "fiche_renseignement",
        title: "Fiche de Renseignement Client",
        data: formData,
      });
      // Mark as submitted
      const docs = await api.get("/documents");
      const ficheDoc = docs.data.find(d => d.doc_type === "fiche_renseignement" && d.client_id === user.user_id);
      if (ficheDoc) {
        await api.put(`/documents/${ficheDoc.document_id}`, { status: "soumis" });
      }
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const currentSection = ONBOARDING_SECTIONS[step];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8" style={{ background: "#0b0811" }} data-testid="onboarding-page">
      <img src={LOGO_URL} alt="Blitzz" className="h-16 mb-6 object-contain" />
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black tracking-tighter">
            Bienvenue, <span style={{ color: "#f7c935" }}>{user?.name?.split(" ")[0] || "Client"}</span>
          </h1>
          <p className="text-slate-400 mt-2">Complétez votre fiche de renseignement pour commencer</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {ONBOARDING_SECTIONS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                i < step ? "bg-green-400 border-green-400 text-[#14101c]" :
                i === step ? "border-[#f7c935] text-[#f7c935]" :
                "border-white/20 text-slate-500"
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              {i < ONBOARDING_SECTIONS.length - 1 && <div className={`w-12 h-0.5 ${i < step ? "bg-green-400" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-5" style={{ color: "#f7c935" }}>{currentSection.title}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {currentSection.fields.map(field => (
              <div key={field.name} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                <label className="text-sm text-slate-400 mb-1.5 block">{field.label} {field.required && <span className="text-red-400">*</span>}</label>
                {field.type === "text" && (
                  <Input value={formData[field.name] || ""} onChange={(e) => updateField(field.name, e.target.value)} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid={`onb-${field.name}`} />
                )}
                {field.type === "textarea" && (
                  <Textarea value={formData[field.name] || ""} onChange={(e) => updateField(field.name, e.target.value)} rows={3} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid={`onb-${field.name}`} />
                )}
                {field.type === "select" && (
                  <Select value={formData[field.name] || ""} onValueChange={(v) => updateField(field.name, v)}>
                    <SelectTrigger className="bg-[#0b0811] border-white/10 text-white" data-testid={`onb-${field.name}`}><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent className="bg-[#14101c] border-white/10 text-white">{field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-6">
            {step > 0 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="border-white/10 text-white hover:bg-white/5">Précédent</Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="border-white/10 text-white hover:bg-white/5">Passer</Button>
            )}
            {step < ONBOARDING_SECTIONS.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">
                Suivant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="onb-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Soumettre</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
